import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { Toaster } from '@/components/ui/sonner';
import { gcalUrl } from '@/lib/gcal';
import AddonReserveButton from '@/components/AddonCheckout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DAY_NAMES = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
const ACCENTS = ['--a1', '--a2', '--a3', '--a4', '--a5'];
const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";

function formatEventDate(iso) {
    if (!iso) return 'Coming soon';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' })
            .replace(/,/g, ' ·');
    } catch { return 'Coming soon'; }
}

function pricingLabel(a) {
    if (a?.external_registration_url) {
        const p = Number(a?.base_price ?? 0);
        return p === 0 ? 'Free · partner-hosted' : `₹${p} · partner-hosted`;
    }
    const cats = a?.ticket_categories_view || [];
    if (cats.length) {
        const avail = cats.filter(c => !c.sold_out && !c.expired);
        if (!avail.length) return 'Sold out';
        const prices = avail.map(c => c.your_price).filter(p => p != null).map(Number);
        if (!prices.length) return 'Members only';
        const mn = Math.min(...prices);
        return mn === 0 ? 'Free · pick a ticket' : `from ₹${mn}`;
    }
    if (a?.your_audience === 'blocked') return 'Members only';
    const p = Number(a?.your_price ?? a?.base_price ?? 0);
    const walkIns = a?.attendance_policy === 'walk_ins';
    if (p === 0) return walkIns ? 'Free · walk-ins welcome' : 'Free · reservations required';
    return walkIns ? `₹${p} · walk-ins welcome` : `₹${p}`;
}

function formatEventTime(startIso, endIso) {
    if (!startIso) return '';
    const fmt = (iso) => { try { return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' }).toLowerCase(); } catch { return ''; } };
    const s = fmt(startIso);
    if (!s) return '';
    const e = endIso ? fmt(endIso) : '';
    return e ? `${s} – ${e}` : s;
}

function deadlineBadge(cats) {
    // Earliest upcoming ticket-tier deadline within 7 days → "Early bird ends in N days"
    const now = Date.now();
    let best = null;
    (cats || []).forEach(c => {
        if (!c.deadline || c.sold_out || c.expired) return;
        const t = new Date(c.deadline).getTime();
        if (Number.isNaN(t) || t <= now) return;
        if (!best || t < best.t) best = { t, name: c.name };
    });
    if (!best) return null;
    const days = Math.ceil((best.t - now) / 86400000);
    if (days > 7) return null;
    return days <= 1 ? `${best.name} ends today!` : `${best.name} ends in ${days} days`;
}

function buildMonth(year, month0, marks, closedSet) {
    const first = new Date(year, month0, 1);
    const firstDow = first.getDay();
    const days = new Date(year, month0 + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push({ label: '', bg: 'transparent', fg: 'inherit', note: '', border: 'transparent' });
    for (let d = 1; d <= days; d++) {
        const m = marks[d];
        const iso = `${year}-${String(month0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const isSunday = new Date(year, month0, d).getDay() === 0;
        const closed = isSunday && closedSet?.has(iso);
        if (m) cells.push({ label: String(d), bg: m.bg, fg: m.fg, note: m.note, border: 'var(--line)', iso, eventId: m.eventId });
        else if (isSunday && !closed) cells.push({ label: String(d), bg: 'var(--a2)', fg: 'var(--o2)', note: '', border: 'var(--line)', iso });
        else cells.push({ label: String(d), bg: 'transparent', fg: 'inherit', note: '', border: 'transparent', iso, closed });
    }
    const monthName = first.toLocaleDateString('en-IN', { month: 'long' });
    return { name: `${monthName} ${year}`, cells };
}

function jumpToEvent(eventId) {
    const el = document.getElementById(`event-card-${eventId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('hydlib-flash');
    void el.offsetWidth;
    el.classList.add('hydlib-flash');
}

export default function Events() {
    const { user, getAuthHeader } = useAuth();
    const [events, setEvents] = useState([]);
    const [closedSundays, setClosedSundays] = useState(new Set());
    const [loaded, setLoaded] = useState(false);
    const [availableMap, setAvailableMap] = useState({});

    useEffect(() => {
        fetch(`${API}/sunday-closures`)
            .then(r => r.json())
            .then(d => setClosedSundays(new Set(Array.isArray(d) ? d : [])))
            .catch(() => { /* ignore */ });
    }, []);

    const loadEvents = useCallback(async () => {
        try {
            const headers = {};
            const token = localStorage.getItem('token');
            if (token) headers.Authorization = `Bearer ${token}`;
            const res = await fetch(`${API}/addons/upcoming`, { headers });
            const data = await res.json();
            const upcoming = Array.isArray(data?.upcoming) ? data.upcoming : [];
            if (upcoming.length) {
                setEvents(upcoming.map((a, i) => {
                    const accent = ACCENTS[i % ACCENTS.length];
                    return {
                        id: a.id,
                        date: formatEventDate(a.event_datetime),
                        title: a.name || 'Untitled',
                        desc: a.description || '',
                        price: pricingLabel(a),
                        time: formatEventTime(a.event_datetime, a.event_end_datetime),
                        gcal: gcalUrl({ title: a.name, start: a.event_datetime, end: a.event_end_datetime, description: a.description, location: a.location }),
                        walkIns: a.attendance_policy === 'walk_ins',
                        bg: `var(${accent})`,
                        fg: `var(--o${accent.slice(-1)})`,
                        tilt: (i % 2 === 0 ? -1 : 1) * (0.4 + (i % 3) * 0.3),
                        image_url: a.image_url,
                        event_datetime: a.event_datetime,
                        isSubscribed: !!a.is_subscribed,
                        spotsRemaining: a.spots_remaining,
                        canSubscribe: a.can_subscribe !== false,
                        externalUrl: a.external_registration_url || null,
                        allowFreeRsvp: !!a.allow_free_rsvp,
                        categories: Array.isArray(a.ticket_categories_view) ? a.ticket_categories_view : [],
                        deadlineBadge: deadlineBadge(a.ticket_categories_view),
                    };
                }));
            }
            if (token) {
                try {
                    const avRes = await fetch(`${API}/addons/available`, { headers });
                    const avData = await avRes.json();
                    const map = {};
                    (Array.isArray(avData) ? avData : []).forEach(a => { map[a.id] = a; });
                    setAvailableMap(map);
                } catch { /* ignore */ }
            }
        } catch { /* keep fallback */ }
        setLoaded(true);
    }, []);

    useEffect(() => { loadEvents(); }, [loadEvents]);

    // Deep-link support: /events#event-card-{id}
    useEffect(() => {
        if (!loaded) return;
        const hash = window.location.hash;
        if (hash.startsWith('#event-card-')) {
            setTimeout(() => jumpToEvent(hash.replace('#event-card-', '')), 350);
        }
    }, [loaded, events.length]);

    const shareWhatsApp = (ev) => {
        const url = `${window.location.origin}/events#event-card-${ev.id}`;
        const text = `Join me at The Hyderabad Library for ${ev.title} on ${ev.date}${ev.time ? `, ${ev.time}` : ''}! ${url}`;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
    };
    const copyLink = async (ev) => {
        const url = `${window.location.origin}/events#event-card-${ev.id}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied! 📋');
        } catch {
            toast.error('Could not copy — please copy the address bar link instead.');
        }
    };

    const rsvpFree = async (ev) => {
        try {
            const res = await fetch(`${API}/addons/${ev.id}/subscribe`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({ free_rsvp: true }),
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.detail || 'Could not RSVP');
            toast.success("You're in! See you there 👋");
            loadEvents();
        } catch (e) {
            toast.error(e.message || 'Could not RSVP');
        }
    };

    // Build 2-month calendar (current + next), marking days that have events
    const now = new Date();
    const buildMarks = (year, month0) => {
        const marks = {};
        events.forEach((e, i) => {
            if (!e.event_datetime) return;
            const d = new Date(e.event_datetime);
            if (d.getFullYear() === year && d.getMonth() === month0) {
                const accent = ACCENTS[i % ACCENTS.length];
                marks[d.getDate()] = {
                    bg: `var(${accent})`,
                    fg: `var(--o${accent.slice(-1)})`,
                    note: (e.title || '').split(':')[0].slice(0, 16),
                    eventId: e.id,
                };
            }
        });
        return marks;
    };
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const months = [
        buildMonth(now.getFullYear(), now.getMonth(), buildMarks(now.getFullYear(), now.getMonth()), closedSundays),
        buildMonth(nextMonth.getFullYear(), nextMonth.getMonth(), buildMarks(nextMonth.getFullYear(), nextMonth.getMonth()), closedSundays),
    ];

    // Next open Sunday (for the warm empty state)
    const nextOpenSunday = () => {
        const d = new Date();
        d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
        let guard = 0;
        const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
        while (closedSundays.has(iso(d)) && guard++ < 8) d.setDate(d.getDate() + 7);
        return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' });
    };

    const monoBtn = {
        cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)',
        fontFamily: mono, fontWeight: 700, fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', padding: '6px 10px',
    };

    return (
        <PublicLayout theme="archive" grid="--a1" gridStrength={34}>
            <Toaster position="bottom-center" />
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a4) 0 5px, transparent 6px)' }} />
            <div className="hydlib-pad" style={{ maxWidth: 1240, margin: '0 auto', padding: '64px 48px' }}>
                <h1 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 52, margin: '0 0 8px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>Sundays at the library</span>
                </h1>
                <p style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 40px' }}>
                    <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>Readings, workshops, open mics — doors open 10.30am–5pm</span>
                </p>

                {/* Floating calendar */}
                <div className="hydlib-calfloat" style={{ float: 'right', width: 420, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 28, margin: '0 0 32px 40px' }}>
                    {months.map((m, i) => (
                        <div key={i} style={{ border: '1px solid var(--line)', background: 'var(--paper)', padding: '22px 24px 26px', boxShadow: '6px 6px 0 color-mix(in oklch, var(--line) 30%, transparent)' }}>
                            <div style={{ fontFamily: serifDisplay, fontSize: 22, letterSpacing: 2, textTransform: 'uppercase', textAlign: 'center', marginBottom: 16 }}>{m.name}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, fontFamily: "'Libre Caslon Text', serif", fontSize: 14, textAlign: 'center' }}>
                                {DAY_NAMES.map(dn => (
                                    <div key={dn} style={{ padding: '6px 0', fontStyle: 'italic', letterSpacing: 1, borderBottom: '1px solid var(--line)', color: 'var(--a1)' }}>{dn}</div>
                                ))}
                                {m.cells.map((c, j) => (
                                    c.eventId ? (
                                        <button
                                            key={j}
                                            className="hydlib-cal-daybtn"
                                            onClick={() => jumpToEvent(c.eventId)}
                                            aria-label={`Jump to event on ${c.iso}`}
                                            data-testid={`calendar-day-${c.iso}`}
                                            style={{ minHeight: 52, padding: '6px 2px', background: c.bg, color: c.fg, border: '1px solid var(--line)', fontFamily: 'inherit', fontSize: 'inherit' }}
                                        >
                                            <div>{c.label}</div>
                                            {c.note && <div style={{ fontFamily: mono, fontSize: 8.5, letterSpacing: 0.3, textTransform: 'uppercase', lineHeight: 1.3, marginTop: 2 }}>{c.note}</div>}
                                            <div style={{ fontSize: 9, marginTop: 1 }}>▾</div>
                                        </button>
                                    ) : (
                                        <div key={j} style={{ minHeight: 52, padding: '6px 2px', background: c.bg, color: c.fg, border: `1px solid ${c.border}`, ...(c.closed ? { textDecoration: 'line-through', opacity: 0.45 } : {}) }}>
                                            <div>{c.label}</div>
                                        </div>
                                    )
                                ))}
                            </div>
                            <div style={{ fontFamily: "'Libre Caslon Text', serif", fontStyle: 'italic', fontSize: 13, letterSpacing: 0.5, marginTop: 12, opacity: 0.75 }}>■ Sundays = open doors, 10.30am–5pm · tap an event day to jump to it</div>
                        </div>
                    ))}
                </div>

                {/* Event index cards */}
                <div style={{ marginBottom: 56 }}>
                    {events.length === 0 ? (
                        <div data-testid="events-empty-state">
                            {[
                                { bg: 'var(--a2)', fg: 'var(--o2)', tilt: -1.5, note: 'nothing on the corkboard yet…' },
                                { bg: 'var(--a3)', fg: 'var(--o3)', tilt: 1, note: `next open Sunday: ${nextOpenSunday()} — drop in, 10.30am–5pm` },
                                { bg: 'var(--a1)', fg: 'var(--o1)', tilt: -0.8, note: 'got an idea? pitch it below ↓' },
                            ].map((n, i) => (
                                <div key={i} style={{ display: 'inline-block', verticalAlign: 'top', background: n.bg, color: n.fg, width: 250, minHeight: 190, padding: '28px 26px 24px', boxSizing: 'border-box', margin: '0 36px 36px 0', boxShadow: '3px 10px 16px color-mix(in oklch, var(--line) 40%, transparent)', transform: `rotate(${n.tilt}deg)` }} data-testid={`event-blank-${i}`}>
                                    <div style={{ fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, opacity: 0.9, transform: 'rotate(-1deg)', marginTop: 40 }}>{n.note}</div>
                                </div>
                            ))}
                        </div>
                    ) : events.map((ev, i) => (
                        <div
                            key={i}
                            id={`event-card-${ev.id}`}
                            className="hydlib-event-card"
                            data-testid={`event-card-${i}`}
                            style={{ display: 'inline-block', verticalAlign: 'top', width: 340, maxWidth: '100%', margin: '0 30px 34px 0', background: 'var(--paper)', color: 'var(--ink)', border: '1px solid var(--line)', boxShadow: '6px 6px 0 color-mix(in oklch, var(--line) 30%, transparent)', transform: `rotate(${ev.tilt}deg)` }}
                        >
                            {/* Date stamp band */}
                            <div style={{ background: ev.bg, color: ev.fg, padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                                <span>{ev.date}</span>
                                {ev.time && <span data-testid={`event-time-${i}`} style={{ opacity: 0.9, fontWeight: 400 }}>{ev.time}</span>}
                            </div>

                            <div style={{ padding: '18px 20px 20px' }}>
                                <div style={{ fontFamily: serifDisplay, fontSize: 26, lineHeight: 1.2 }}>{ev.title}</div>
                                {ev.desc && <div style={{ fontSize: 14, marginTop: 10, opacity: 0.85, lineHeight: 1.55 }}>{ev.desc}</div>}

                                <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1, marginTop: 12, color: 'var(--a1)' }}>{ev.price}</div>

                                {!user && !ev.externalUrl && ev.categories.length > 0 && (
                                    <div style={{ fontFamily: mono, fontSize: 11, letterSpacing: 0.5, marginTop: 6, opacity: 0.85, lineHeight: 1.7 }} data-testid={`event-cats-guest-${i}`}>
                                        {ev.categories.map((c, ci) => (
                                            <div key={c.id || ci}>
                                                {c.name} — {c.expired ? 'ended' : c.sold_out ? 'sold out' : (c.your_price != null ? (Number(c.your_price) === 0 ? 'Free' : `₹${c.your_price}`) : `₹${c.member_price} members`)}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Urgency badges */}
                                {ev.spotsRemaining != null && ev.spotsRemaining > 0 && ev.spotsRemaining <= 5 && (
                                    <div style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 700, marginTop: 8, color: 'var(--a2)', transform: 'rotate(-1deg)', width: 'fit-content' }} data-testid={`event-urgency-spots-${i}`}>
                                        only {ev.spotsRemaining} spot{ev.spotsRemaining === 1 ? '' : 's'} left!
                                    </div>
                                )}
                                {ev.deadlineBadge && (
                                    <div style={{ fontFamily: "'Caveat', cursive", fontSize: 19, fontWeight: 700, marginTop: 4, color: 'var(--a4)', transform: 'rotate(1deg)', width: 'fit-content' }} data-testid={`event-urgency-deadline-${i}`}>
                                        {ev.deadlineBadge}
                                    </div>
                                )}

                                {/* Attendance options can coexist: paid tickets + free RSVP + walk-ins */}
                                {ev.walkIns && (
                                    <div style={{ fontFamily: mono, fontWeight: 700, fontSize: 10.5, letterSpacing: 1, textTransform: 'uppercase', marginTop: 10, width: 'fit-content', border: '1px dashed var(--ink)', padding: '4px 10px', opacity: 0.75 }} data-testid={`event-walkin-tag-${i}`}>
                                        🚶 Walk-ins welcome
                                    </div>
                                )}

                                {/* Actions */}
                                {ev.externalUrl ? (
                                    <a
                                        href={ev.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => { fetch(`${API}/addons/${ev.id}/track-external-click`, { method: 'POST' }).catch(() => { /* ignore */ }); }}
                                        style={{ display: 'inline-block', marginTop: 14, border: '1px solid var(--ink)', color: 'var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'none', padding: '7px 14px' }}
                                        data-testid={`event-external-register-${i}`}
                                    >
                                        Register on host&apos;s site ↗
                                    </a>
                                ) : ev.isSubscribed ? (
                                    <span style={{ display: 'inline-block', marginTop: 14, border: '1px solid var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 14px', background: 'color-mix(in oklch, var(--a3) 18%, transparent)' }}>You&apos;re in ✓</span>
                                ) : ev.categories.length > 0 && user && ev.id && availableMap[ev.id] ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, marginTop: 6 }} data-testid={`event-categories-${i}`}>
                                        {ev.categories.map((c, ci) => (
                                            (c.your_price == null && !c.sold_out && !c.expired) ? (
                                                <span key={c.id || ci} style={{ display: 'inline-block', marginTop: 8, border: '1px dashed var(--ink)', color: 'var(--ink)', opacity: 0.6, fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 14px' }} data-testid={`event-cat-members-only-${i}-${ci}`}>{c.name} — members only</span>
                                            ) : (
                                                <AddonReserveButton key={c.id || ci} addon={availableMap[ev.id]} category={c} user={user} getAuthHeader={getAuthHeader} fg="var(--ink)" onDone={loadEvents} />
                                            )
                                        ))}
                                    </div>
                                ) : user && ev.id && availableMap[ev.id] ? (
                                    <AddonReserveButton addon={availableMap[ev.id]} user={user} getAuthHeader={getAuthHeader} fg="var(--ink)" onDone={loadEvents} />
                                ) : user && ev.id ? (
                                    <span style={{ display: 'inline-block', marginTop: 14, fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 0', opacity: 0.8 }} data-testid={`event-unavailable-${i}`}>{ev.spotsRemaining === 0 ? 'Fully booked' : 'Members only'}</span>
                                ) : ev.walkIns ? (
                                    <span style={{ display: 'inline-block', marginTop: 14, fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 0' }} data-testid={`event-walkin-${i}`}>Just show up ✓</span>
                                ) : (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 14 }}>
                                        <Link
                                            to="/signup?mode=events&redirect=/events"
                                            style={{ display: 'inline-block', border: '1px solid var(--ink)', color: 'var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'none', padding: '7px 14px' }}
                                            data-testid={`event-reserve-${i}`}
                                        >
                                            Reserve a spot
                                        </Link>
                                        <span style={{ fontSize: 12.5, opacity: 0.75 }}>free account · or <Link to="/login" style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }} data-testid={`event-login-${i}`}>log in</Link></span>
                                    </div>
                                )}

                                {/* Free RSVP — offered alongside paid tiers when enabled */}
                                {ev.allowFreeRsvp && !ev.isSubscribed && !ev.externalUrl && (
                                    user ? (
                                        <button
                                            onClick={() => rsvpFree(ev)}
                                            data-testid={`event-free-rsvp-${i}`}
                                            style={{ display: 'block', marginTop: 8, cursor: 'pointer', background: 'transparent', border: '1px dashed var(--ink)', color: 'var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', padding: '7px 14px' }}
                                        >
                                            RSVP free — just attending
                                        </button>
                                    ) : (
                                        <Link
                                            to="/signup?mode=events&redirect=/events"
                                            data-testid={`event-free-rsvp-login-${i}`}
                                            style={{ display: 'inline-block', marginTop: 8, border: '1px dashed var(--ink)', color: 'var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'none', padding: '7px 14px' }}
                                        >
                                            RSVP free — quick sign-up
                                        </Link>
                                    )
                                )}

                                {/* Footer: calendar + share */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px dashed var(--line)' }}>
                                    {ev.gcal && (
                                        <a href={ev.gcal} target="_blank" rel="noopener noreferrer" data-testid={`event-gcal-${i}`} style={{ ...monoBtn, textDecoration: 'none', display: 'inline-block' }}>+ Calendar</a>
                                    )}
                                    <button onClick={() => shareWhatsApp(ev)} data-testid={`event-share-whatsapp-${i}`} style={monoBtn}>WhatsApp ↗</button>
                                    <button onClick={() => copyLink(ev)} data-testid={`event-share-copy-${i}`} style={monoBtn}>Copy link</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pitch an event */}
                <div data-testid="pitch-event-strip" style={{ clear: 'both', border: '1px solid var(--line)', borderTop: '6px solid var(--a2)', background: 'var(--paper)', padding: '28px 30px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                    <div>
                        <div style={{ fontFamily: serifDisplay, fontSize: 26, marginBottom: 4 }}>Got an idea for an event?</div>
                        <div style={{ fontSize: 15, opacity: 0.9 }}>Sundays are community-run — pitch a reading, a workshop, a club. We&apos;ll bring the space.</div>
                    </div>
                    <a
                        href="mailto:thehyderabadlibrary@gmail.com?subject=Sunday%20event%20pitch"
                        className="hydlib-highlight"
                        style={{
                            fontFamily: mono, fontWeight: 700, fontSize: 13,
                            letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink)',
                            padding: '12px 22px',
                            background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 58%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 42%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 64%, var(--paper)) 94%, transparent 98.5%)`,
                        }}
                    >
                        Pitch an event →
                    </a>
                </div>
            </div>
            {!loaded && <div style={{ display: 'none' }} data-testid="events-loading" />}
        </PublicLayout>
    );
}
